import { DeliveryOption } from '@web-foodis/shared-models';
import {
  OrderCartAddonItem,
  OrderCartProductItem,
} from './model/order-basket.model';

/** All price amounts are integer cents, including the raw pickup subtotal. */
export interface CartPriceEstimate {
  initialPrice: number;
  totalDeliveryPrice: number;
  subtotalPrice: number;
  deliveryPrice: number;
  extraDeliveryDiscount: number;
  totalPrice: number;
  minOrderAmountCents: number;
  minOrderAmountReached: boolean;
}

export function calculateLinePrice(
  product: OrderCartProductItem,
  deliveryOption: DeliveryOption
): number {
  const productPrice =
    deliveryOption === DeliveryOption.DELIVERY
      ? product.productPriceDelivery
      : product.productPrice;
  const addonPrice = (product.addons ?? []).reduce(
    (total, addon) => total + addon.addonPrice * addon.quantity,
    0
  );

  return (productPrice + addonPrice) * product.quantity;
}

export function calculateCartPrice(
  items: readonly OrderCartProductItem[],
  deliveryOption: DeliveryOption,
  minOrderAmountEuros = 0,
  deliveryFeeCents = 600
): CartPriceEstimate {
  const isDelivery = deliveryOption === DeliveryOption.DELIVERY;
  const initialPrice = items.reduce(
    (total, item) => total + calculateLinePrice(item, DeliveryOption.PICKUP),
    0
  );
  const totalDeliveryPrice = isDelivery
    ? items.reduce(
        (total, item) => total + calculateLinePrice(item, DeliveryOption.DELIVERY),
        0
      )
    : 0;
  const subtotalPrice = isDelivery ? totalDeliveryPrice : initialPrice;
  const minOrderAmountCents = isDelivery
    ? Math.round(minOrderAmountEuros * 100)
    : 0;
  const estimate: CartPriceEstimate = {
    initialPrice,
    totalDeliveryPrice,
    subtotalPrice,
    deliveryPrice: 0,
    extraDeliveryDiscount: 0,
    totalPrice: initialPrice,
    minOrderAmountCents,
    minOrderAmountReached: !isDelivery || subtotalPrice >= minOrderAmountCents,
  };

  if (!isDelivery || items.length === 0) {
    return estimate;
  }

  const deliveryMarkup = totalDeliveryPrice - initialPrice;

  return {
    ...estimate,
    deliveryPrice: Math.max(0, deliveryFeeCents - deliveryMarkup),
    extraDeliveryDiscount: Math.max(0, deliveryMarkup - deliveryFeeCents),
    totalPrice: initialPrice + deliveryFeeCents,
  };
}

function normalizeItem(item: OrderCartProductItem): OrderCartProductItem {
  const addonsBySelection = new Map<string, OrderCartAddonItem>();

  for (const addon of item.addons ?? []) {
    const selectionKey = JSON.stringify([addon.addonGroupId, addon.addonId]);
    const existing = addonsBySelection.get(selectionKey);

    if (existing) {
      existing.quantity += addon.quantity;
    } else {
      addonsBySelection.set(selectionKey, { ...addon });
    }
  }

  const addons = Array.from(addonsBySelection.values()).sort((left, right) => {
    if (left.addonGroupId !== right.addonGroupId) {
      return left.addonGroupId < right.addonGroupId ? -1 : 1;
    }

    if (left.addonId !== right.addonId) {
      return left.addonId < right.addonId ? -1 : 1;
    }

    return 0;
  });

  return {
    ...item,
    addons,
    specialInstructions: (item.specialInstructions ?? '').trim(),
  };
}

function normalizedItemKey(item: OrderCartProductItem): string {
  return JSON.stringify([
    item.productId,
    item.addons.map((addon) => [
      addon.addonGroupId,
      addon.addonId,
      addon.quantity,
    ]),
    item.specialInstructions,
  ]);
}

/** Prices and display metadata do not identify a selected configuration. */
export function getCartItemKey(item: OrderCartProductItem): string {
  return normalizedItemKey(normalizeItem(item));
}

export function normalizeCartItems(
  items: readonly OrderCartProductItem[]
): OrderCartProductItem[] {
  const itemsByConfiguration = new Map<string, OrderCartProductItem>();

  for (const item of items) {
    const normalized = normalizeItem(item);
    const key = normalizedItemKey(normalized);
    const existing = itemsByConfiguration.get(key);

    if (existing) {
      existing.quantity += normalized.quantity;
    } else {
      itemsByConfiguration.set(key, normalized);
    }
  }

  return Array.from(itemsByConfiguration.values());
}
